import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Mail, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { DatePickerField } from "@/components/DatePickerField";
import { SelectField } from "@/components/SelectField";

const serviceOptions = [
  { value: "domestic", label: "Domestic Tour (India)" },
  { value: "international", label: "International Tour" },
  { value: "visa", label: "Visa Service" },
];

const initialForm = {
  full_name: "",
  email: "",
  phone: "",
  destination: "",
  travel_date: "",
  travelers: 2,
  message: "",
};

export const EnquiryFormSection = ({ service, setService, initialDate, packageInfo }) => {
  const settings = useSettings();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (initialDate) setForm((f) => ({ ...f, travel_date: initialDate }));
  }, [initialDate]);

  useEffect(() => {
    if (packageInfo?.title) setForm((f) => ({ ...f, destination: packageInfo.title }));
  }, [packageInfo]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/enquiries", {
        ...form,
        service_type: service,
        travelers: Number(form.travelers) || 1,
        ...(packageInfo?.id ? { package_id: packageInfo.id } : {}),
      });
      setConfirmation(res.data);
      toast.success("Enquiry received — our travel designer will reach out within 24 hours.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(initialForm);
    setConfirmation(null);
  };

  return (
    <section id="enquiry" data-testid="enquiry-section" className="py-20 md:py-28 px-4 sm:px-8 lg:px-16">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-4">05 — Begin Your Journey</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-cream mb-6">
            Tell us where your heart <span className="italic text-gold">is pointing.</span>
          </h2>
          <p className="text-base text-mist leading-relaxed mb-10 max-w-md">
            Share a few details and a dedicated travel designer will craft a shortlist of journeys —
            with transparent pricing — within 24 hours.
          </p>

          <div className="space-y-5">
            <a href={`tel:${settings.phone.replace(/\s/g, "")}`} data-testid="contact-phone" className="flex items-center gap-4 text-mist hover:text-gold transition-colors">
              <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center text-gold"><Phone size={16} /></span>
              <span className="text-sm">{settings.phone}</span>
            </a>
            <a href={`mailto:${settings.email}`} data-testid="contact-email" className="flex items-center gap-4 text-mist hover:text-gold transition-colors">
              <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center text-gold"><Mail size={16} /></span>
              <span className="text-sm">{settings.email}</span>
            </a>
            <div className="flex items-center gap-4 text-mist">
              <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center text-gold"><MapPin size={16} /></span>
              <span className="text-sm">{settings.address}</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-7"
        >
          <div className="rounded-3xl border border-gold/20 bg-card/70 backdrop-blur-sm p-6 sm:p-10">
            {confirmation ? (
              <div className="text-center py-10" data-testid="enquiry-success">
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 14 }}
                >
                  <CheckCircle2 size={64} className="mx-auto text-gold" strokeWidth={1.5} />
                </motion.div>
                <h3 className="font-display text-2xl sm:text-3xl text-cream mt-6 mb-3">
                  Enquiry received, <span className="italic text-gold">{form.full_name.split(" ")[0]}.</span>
                </h3>
                <p className="text-sm text-mist max-w-md mx-auto leading-relaxed">
                  Your reference is <span className="font-mono text-gold" data-testid="enquiry-reference">{confirmation.reference}</span>.
                  A travel designer will call you within 24 hours with a tailored shortlist.
                </p>
                <button
                  onClick={reset}
                  data-testid="enquiry-another-btn"
                  className="mt-8 text-sm text-gold border border-gold/40 px-7 py-3 rounded-full hover:bg-gold hover:text-ink transition-colors"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="enquiry-form" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Full Name *</label>
                    <input required value={form.full_name} onChange={update("full_name")} data-testid="enquiry-name-input" className="field-input" placeholder="Aarav Mehta" />
                  </div>
                  <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Phone *</label>
                    <input required value={form.phone} onChange={update("phone")} data-testid="enquiry-phone-input" className="field-input" placeholder="+91 98765 43210" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Email *</label>
                    <input required type="email" value={form.email} onChange={update("email")} data-testid="enquiry-email-input" className="field-input" placeholder="you@example.com" />
                  </div>
                  <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Service Type *</label>
                    <SelectField
                      value={service}
                      onChange={setService}
                      options={serviceOptions}
                      testid="enquiry-service-select"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Dream Destination</label>
                    <input value={form.destination} onChange={update("destination")} data-testid="enquiry-destination-input" className="field-input" placeholder="Maldives, Europe…" />
                  </div>
                  <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Travel Date</label>
                    <DatePickerField
                      value={form.travel_date}
                      onChange={(v) => setForm((f) => ({ ...f, travel_date: v }))}
                      testid="enquiry-date-input"
                      placeholder="Choose travel date"
                      minDate={new Date()}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Travellers</label>
                    <input type="number" min="1" max="100" value={form.travelers} onChange={update("travelers")} data-testid="enquiry-travelers-input" className="field-input" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Anything else?</label>
                  <textarea rows={3} value={form.message} onChange={update("message")} data-testid="enquiry-message-input" className="field-input resize-none" placeholder="Honeymoon, Jain meals, wheelchair access…" />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="enquiry-submit-btn"
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gold text-ink font-semibold text-sm px-10 py-4 rounded-full hover:bg-gold-hover transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending…" : "Send Enquiry"}
                  <Send size={15} />
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
